import { useEffect, useState } from "react"
import type { Server } from "@mc/types"
import { AuditAction } from "@mc/types"
import { listAuditLog, type AuditLogQuery } from "../api/audit.js"
import { listServers } from "../api/servers.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { useAuth } from "../auth/AuthProvider.js"

const ACTION_OPTIONS = Object.values(AuditAction)

function humanizeAction(action: string): string {
  return action.replace(/[._]/g, " ")
}

type PageState = { status: "loading" } | { status: "ready" } | { status: "error"; message: string }

/**
 * `/audit` (FR section 13). host_owner: cualquier combinación de filtros,
 * `serverId` es opcional. Cualquier otro rol: `serverId` es obligatorio
 * (el Gateway responde 403 sin él, ver routes/audit.ts), así que esta
 * página lo fuerza en la UI en vez de dejar que el primer request falle:
 * un selector de "tus servidores" reemplaza el filtro libre de serverId
 * para quien no es host_owner.
 */
export function AuditLogPage() {
  const { user } = useAuth()
  const isHostOwner = user?.systemRole === "host_owner"

  const [servers, setServers] = useState<Server[]>([])
  const [entries, setEntries] = useState<Awaited<ReturnType<typeof listAuditLog>>["entries"]>([])
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [filters, setFilters] = useState<AuditLogQuery>({})

  useEffect(() => {
    listServers()
      .then(({ servers }) => setServers(servers))
      .catch(() => setServers([]))
  }, [])

  useEffect(() => {
    if (!isHostOwner && !filters.serverId) {
      setState({ status: "ready" })
      setEntries([])
      return
    }
    setState({ status: "loading" })
    listAuditLog(filters)
      .then(({ entries }) => {
        setEntries(entries)
        setState({ status: "ready" })
      })
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isHostOwner])

  const selectStyle: React.CSSProperties = {
    padding: "8px 10px",
    fontSize: "var(--text-sm)",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border-default)",
    background: "var(--bg-surface)",
    color: "var(--text-primary)",
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Auditoría</h1>
        <p style={{ color: "var(--text-muted)" }}>
          {isHostOwner
            ? "Registro de todas las acciones del sistema. Filtrá por servidor, usuario, tipo de acción o fecha."
            : "Registro de acciones de un servidor donde tenés acceso."}
        </p>
      </div>

      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Servidor</label>
          <select
            value={filters.serverId ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, serverId: e.target.value || undefined }))}
            style={selectStyle}
          >
            <option value="">{isHostOwner ? "Todos" : "Elegí un servidor…"}</option>
            {servers.map((server) => (
              <option key={server.serverId} value={server.serverId}>
                {server.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Acción</label>
          <select
            value={filters.action ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, action: (e.target.value || undefined) as typeof f.action }))}
            style={selectStyle}
          >
            <option value="">Todas</option>
            {ACTION_OPTIONS.map((action) => (
              <option key={action} value={action}>
                {humanizeAction(action)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Desde</label>
          <input
            type="date"
            value={filters.from ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || undefined }))}
            style={selectStyle}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          <label style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Hasta</label>
          <input
            type="date"
            value={filters.to ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))}
            style={selectStyle}
          />
        </div>

        {(filters.action || filters.from || filters.to || filters.serverId) && (
          <Button size="sm" variant="ghost" onClick={() => setFilters({})}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {!isHostOwner && !filters.serverId && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
          Elegí un servidor para ver su registro de auditoría.
        </p>
      )}

      {state.status === "loading" && (
        <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
          <Spinner size="lg" />
        </div>
      )}

      {state.status === "error" && (
        <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
          {state.message}
        </p>
      )}

      {state.status === "ready" && (isHostOwner || filters.serverId) && entries.length === 0 && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>No hay entradas para estos filtros.</p>
      )}

      {state.status === "ready" && entries.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border-default)", textAlign: "left" }}>
                <th style={{ padding: "var(--space-2) var(--space-3)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>Cuándo</th>
                <th style={{ padding: "var(--space-2) var(--space-3)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>Actor</th>
                <th style={{ padding: "var(--space-2) var(--space-3)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>Acción</th>
                <th style={{ padding: "var(--space-2) var(--space-3)", color: "var(--text-muted)", fontWeight: "var(--font-weight-medium)" }}>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} style={{ borderBottom: "1px solid var(--border-default)" }}>
                  <td style={{ padding: "var(--space-2) var(--space-3)", whiteSpace: "nowrap", color: "var(--text-muted)" }}>
                    {new Date(entry.occurredAt).toLocaleString()}
                  </td>
                  <td style={{ padding: "var(--space-2) var(--space-3)" }}>
                    {entry.actorUsername}
                    {entry.actorType === "api_token" && (
                      <span style={{ marginLeft: "var(--space-2)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                        (api token)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "var(--space-2) var(--space-3)" }}>{humanizeAction(entry.action)}</td>
                  <td style={{ padding: "var(--space-2) var(--space-3)", color: "var(--text-muted)" }}>{entry.target ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
